import { ChevronDown, File as FileGlyph, Globe } from 'lucide-react';
import type * as React from 'react';

import { ConnectedServiceIcon } from '../../atoms/ConnectedServiceIcon';
import { GridSensitivityChip, type GridSensitivity } from '../../atoms/GridSensitivityChip';
import { TruncatedCellText } from '../../atoms/TruncatedCellText';
import { DocumentTypeIcon, FileTypeIcon } from '../../icons';
import {
  resolveFileTypeIcon,
  resolveLocationIcon,
  resolveUserTypeBadge,
} from '../../lib/access-legitimacy-resolvers';
import { humanizeLabel } from '../../lib/humanize-label';
import { cn } from '../../lib/utils';
import { UserTypeBadge } from '../UserTypeBadge';

const EMPTY = '—';

export interface EventIdentityRowProps {
  actor: string;
  /** The file's name in its source system; null or omitted renders "—". */
  fileName?: string | null;
  /** The file's link in its source system; with a name, the name opens it in a new tab. */
  fileUrl?: string | null;
  /** User-type label (lowercase), or null. Resolved to a badge descriptor; null
   *  renders no badge (the curated audit source may have no user-type). */
  userType: string | null;
  /** ISO date string for the event timestamp. */
  dateTime: string;
  /** Document-type label (lowercase), or null. */
  documentType: string | null;
  /** Document subject, or null. */
  documentSubject: string | null;
  /** Primary topic label (lowercase), or null. */
  topic: string | null;
  /** File-type label (lowercase), or null. */
  fileType: string | null;
  /** Detected sensitivity (entity + severity), or null. */
  sensitivity: GridSensitivity | null;
  /** Location label (lowercase), or null. */
  location: string | null;
  /** Formats an ISO date string. */
  formatDateTime: (iso: string) => string;
  /** Click handler for the Take Action CTA. The button renders only when this is set
   *  (dormant for the POV, TDEV-1130). */
  onTakeAction?: () => void;
  takeActionLabel?: string;
  className?: string;
}

/**
 * `EventIdentityRow` — dense 2-row identity strip rendered at the top of an
 * event detail surface (Figma `5:10184`). Consumes the live wire labels and
 * resolves icons / the severity-driven sensitivity chip internally (design v6
 * §3.3), so the drawer hands it event fields directly. Null taxonomy values
 * render the "—" placeholder. Pure presentational, no state.
 */
export function EventIdentityRow({
  actor,
  fileName,
  fileUrl,
  userType,
  dateTime,
  documentType,
  documentSubject,
  topic,
  fileType,
  sensitivity,
  location,
  formatDateTime,
  onTakeAction,
  takeActionLabel = 'Take Action',
  className,
}: EventIdentityRowProps): React.ReactElement {
  const badge = userType !== null ? resolveUserTypeBadge(userType) : null;
  const fileIcon = fileType !== null ? resolveFileTypeIcon(fileType) : null;
  const brandIcon = location !== null ? resolveLocationIcon(location) : null;

  return (
    <div className={cn('flex items-end gap-4', className)}>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {/* Top row — actor + file name + date + document type chip (Figma 5:10185 row 1). */}
        <div className="flex flex-wrap items-center gap-1">
          <div className="flex h-[22px] shrink-0 items-center gap-1">
            {badge !== null && (
              <UserTypeBadge type={badge.type} isPrivileged={badge.isExternal} size="sm" />
            )}
            <span className="font-outfit text-[10px] leading-[1.2] text-dawn-black">{actor}</span>
          </div>
          <div className="flex h-[22px] min-w-0 max-w-[240px] items-center px-2">
            {fileName && fileUrl ? (
              <a
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="event-identity-file-link"
                className="flex min-w-0 items-center hover:underline"
              >
                <TruncatedCellText
                  data-testid="event-identity-file-name"
                  text={fileName}
                  className="font-outfit text-dawn-orange"
                />
              </a>
            ) : fileName ? (
              <TruncatedCellText
                data-testid="event-identity-file-name"
                text={fileName}
                className="font-outfit"
              />
            ) : (
              <span
                data-testid="event-identity-file-name"
                className="font-outfit text-[10px] leading-[1.2] text-grey-400"
              >
                {EMPTY}
              </span>
            )}
          </div>
          <div className="flex h-[22px] shrink-0 items-center px-2">
            <span className="whitespace-pre font-outfit text-[10px] capitalize leading-[1.2] text-dawn-black">
              {formatDateTime(dateTime)}
            </span>
          </div>
          <div className="flex h-[22px] shrink-0 items-center px-2">
            {documentType === null ? (
              <span className="font-outfit text-[10px] leading-[1.2] text-grey-400">{EMPTY}</span>
            ) : (
              <span className="inline-flex items-center gap-[2px] rounded-[2px] bg-grey-100 px-1 py-0.5">
                <DocumentTypeIcon size={14} className="shrink-0 text-grey-600" />
                <span className="font-outfit text-[10px] leading-[1.2] text-dawn-black">
                  {humanizeLabel(documentType)}
                </span>
              </span>
            )}
          </div>
        </div>
        {/* Bottom row — subject + topic + file + sensitivity + location (Figma 5:10185 row 2). */}
        <div className="flex min-w-0 flex-nowrap items-center gap-1">
          <div className="flex h-[22px] min-w-0 flex-1 items-center px-2">
            <span className="block w-full max-w-[121.31px] truncate font-outfit text-[10px] leading-[1.2] text-dawn-black">
              {documentSubject ?? EMPTY}
            </span>
          </div>
          <div className="flex h-[22px] min-w-0 flex-1 items-center px-2">
            <span className="block w-full max-w-[106.823px] truncate font-outfit text-[10px] capitalize leading-[1.2] text-dawn-black">
              {topic !== null ? humanizeLabel(topic) : EMPTY}
            </span>
          </div>
          <div className="flex h-[22px] shrink-0 items-center gap-1 px-2">
            {fileType === null ? (
              <span className="font-outfit text-[10px] leading-[1.2] text-grey-400">{EMPTY}</span>
            ) : (
              <>
                {fileIcon !== null ? (
                  <FileTypeIcon name={fileIcon} size={14} className="shrink-0" />
                ) : (
                  <FileGlyph
                    width={14}
                    height={14}
                    className="shrink-0 text-grey-500"
                    aria-hidden="true"
                  />
                )}
                <span className="font-outfit text-[10px] capitalize leading-[1.2] text-dawn-black">
                  {humanizeLabel(fileType)}
                </span>
              </>
            )}
          </div>
          <div className="flex h-[22px] shrink-0 items-center px-2">
            <GridSensitivityChip sensitivity={sensitivity} />
          </div>
          <div className="flex h-[22px] shrink-0 items-center gap-1 px-2">
            {location === null ? (
              <span className="font-outfit text-[10px] leading-[1.2] text-grey-400">{EMPTY}</span>
            ) : (
              <>
                <span
                  aria-hidden="true"
                  className="inline-flex h-[18px] w-[18px] items-center justify-center overflow-hidden rounded-[2px] bg-grey-100"
                >
                  {brandIcon !== null ? (
                    <ConnectedServiceIcon service={brandIcon} size={14} />
                  ) : (
                    <Globe width={14} height={14} className="text-grey-500" />
                  )}
                </span>
                <span className="font-outfit text-[10px] capitalize leading-[1.2] text-dawn-black">
                  {humanizeLabel(location)}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
      {onTakeAction && (
        <button
          type="button"
          onClick={onTakeAction}
          className="inline-flex h-[26px] shrink-0 items-center gap-1 rounded-[4px] bg-dawn-orange px-[10px] py-[8px] font-outfit text-[13px] leading-[1.3] text-dawn-white"
        >
          {takeActionLabel}
          <ChevronDown className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

EventIdentityRow.displayName = 'EventIdentityRow';
